import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";

type Tone = "info" | "success" | "warn" | "error";

// Light-mode raw palette + dark-mode pair on each tone. ``-900/30``
// bg + ``-200`` text + ``-800/40`` border keeps each semantic
// readable in dark mode without painting a glowing tile on the
// canvas. (2026-05-03 dogfood - alerts looked harsh in dark mode.)
const TONE: Record<Tone, { wrapper: string; icon: ReactNode }> = {
  info: {
    wrapper: "border-line bg-surface-muted text-foreground",
    icon: <Info className="h-4 w-4" aria-hidden="true" />,
  },
  success: {
    wrapper:
      "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/40 dark:bg-emerald-900/30 dark:text-emerald-200",
    icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
  },
  warn: {
    wrapper:
      "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/40 dark:bg-amber-900/30 dark:text-amber-200",
    icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
  },
  error: {
    wrapper:
      "border-red-200 bg-red-50 text-red-800 dark:border-red-800/40 dark:bg-red-900/30 dark:text-red-200",
    icon: <AlertCircle className="h-4 w-4" aria-hidden="true" />,
  },
};

export interface AlertProps {
  readonly tone?: Tone;
  readonly title?: string;
  readonly children: ReactNode;
  readonly className?: string;
}

/** Inline status alert. Used for non-blocking errors, warnings,
 *  and confirmations. For modal/blocking notices use a Dialog. */
export function Alert({ tone = "info", title, children, className }: AlertProps) {
  const t = TONE[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm",
        t.wrapper,
        className,
      )}
    >
      <span className="mt-0.5 shrink-0">{t.icon}</span>
      <div className="space-y-0.5">
        {title && <div className="font-medium leading-tight">{title}</div>}
        <div className="leading-snug">{children}</div>
      </div>
    </div>
  );
}
